import React, { useEffect, useRef } from 'react';
import { StyleSheet, Animated, TouchableOpacity } from 'react-native';

import { Box } from '@components/Restyle/index';
import Text from '@components/Restyle/Text';
import Icon from '@components/shared/Icon/Icon';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import theme from '@themes/theme';

type TaskCreatedToastProps = {
  duration: number;
  onPress?: () => void;
  message?: string;
  title?: string;
  iconName?: keyof typeof Images | keyof typeof documentIcons;
  isFailedToast?: boolean;
  hasRightArrow?: boolean;
  minWidth?: number;
};

export const TaskCreatedToast: React.FC<TaskCreatedToastProps> = ({
  duration,
  onPress,
  message,
  iconName,
  title = 'Task Created',
  hasRightArrow = false,
  minWidth = 240,
  isFailedToast = false,
}) => {
  const progressAnim = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 1,
      duration: duration,
      useNativeDriver: false,
    }).start();
  }, [progressAnim, duration]);

  const handlePress = () => {
    onPress?.();
  };

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={handlePress}
      accessibilityLabel={message}
      style={styles.toastContainer}>
      <Box
        px='m'
        flexDirection='row'
        height={76}
        alignItems='center'
        justifyContent='center'
        minWidth={minWidth}>
        <Icon
          marginRight='s'
          name={iconName ?? (isFailedToast ? 'AlertTriangle' : 'Check')}
          height={24}
          width={24}
          color='white'
        />
        <Box>
          <Text flex={1} variant='heading2' color='white'>
            {title}
          </Text>
          <Text
            flex={1}
            numberOfLines={1}
            ellipsizeMode='tail'
            variant='body'
            color='white'>
            {(message ?? '').length <= 25 || minWidth !== 240
              ? message
              : message?.substring(0, 24) + '...'}
          </Text>
        </Box>
        {hasRightArrow && (
          <Icon
            marginLeft='s'
            name='ArrowRight'
            height={24}
            width={24}
            color='white'
          />
        )}
      </Box>

      <Animated.View
        style={[
          styles.progressBar,
          isFailedToast && { backgroundColor: theme.colors.alertRed },
          {
            width: progressAnim.interpolate({
              inputRange: [0, 1],
              outputRange: ['0%', '100%'],
            }),
          },
        ]}
      />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  toastContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'black',
    borderRadius: theme.borderRadii.xs,
    position: 'relative',
    overflow: 'hidden',
  },

  progressBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    height: 4,
    backgroundColor: theme.colors.greenBrand,
  },
});
